import type { Builder } from '@/builders/types'
import type { Position } from '@/voxelMath'
import type { VoxelData } from '@/voxelTypes'
import type { EventTuple } from '@/events/types'
import { getShapeBounds } from '@/shapes'
import { Quaternion, Vector3 } from '@/math'

export type ShapeKind = 'sphere' | 'cylinder' | 'cone'

export type ShapeBuilderParams = {
  type: ShapeKind
  center: Position
  radius: number
  height?: number
  axis?: 'x' | 'y' | 'z'
  axisSign?: 1 | -1
  voxel: VoxelData
  rotation?: { x:number;y:number;z:number;w:number }
}

type Translate = { x: number; y: number; z: number }
type QuaternionLiteral = NonNullable<ShapeBuilderParams['rotation']>

const IDENTITY_ROTATION: QuaternionLiteral = { x: 0, y: 0, z: 0, w: 1 }
const QUATERNION_EPSILON = 1e-6

function add(a: Position, b: Translate | null | undefined): Position {
  if (!b) return { x: a.x, y: a.y, z: a.z }
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }
}

function isIdentityRotation(rotation: QuaternionLiteral | undefined): boolean {
  if (!rotation) return true
  return (
    Math.abs(rotation.x) < QUATERNION_EPSILON &&
    Math.abs(rotation.y) < QUATERNION_EPSILON &&
    Math.abs(rotation.z) < QUATERNION_EPSILON &&
    Math.abs(rotation.w - 1) < QUATERNION_EPSILON
  )
}

function axisQuaternion(axis: 'x' | 'y' | 'z' = 'y', axisSign: 1 | -1 = 1): Quaternion {
  const target = axis === 'x'
    ? new Vector3(1, 0, 0)
    : axis === 'y'
      ? new Vector3(0, 1, 0)
      : new Vector3(0, 0, 1)
  target.multiplyScalar(axisSign)
  return new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), target)
}

function toLiteral(q: Quaternion): QuaternionLiteral {
  return { x: q.x, y: q.y, z: q.z, w: q.w }
}

function effectiveRotation(params: ShapeBuilderParams): QuaternionLiteral | undefined {
  const rotation = params.rotation
  if (!rotation || isIdentityRotation(rotation)) return undefined

  const delta = new Quaternion(rotation.x, rotation.y, rotation.z, rotation.w).normalize()
  const base = axisQuaternion(params.axis || 'y', params.axisSign ?? 1)
  return toLiteral(delta.multiply(base).normalize())
}

function shapeEventPayload(params: ShapeBuilderParams, center: Position) {
  const rotation = effectiveRotation(params)
  return {
    type: params.type,
    center,
    radius: params.radius,
    height: params.height,
    axis: params.axis || 'y',
    axisSign: params.axisSign ?? 1,
    voxel: params.voxel,
    ...(rotation ? { rotation } : {}),
  }
}

export function makeShapeBuilder(params: ShapeBuilderParams, translate: Translate | null = null): Builder<ShapeBuilderParams> {
  const state = { params: { ...params }, translate: translate ? { ...translate } : null }

  const self: Builder<ShapeBuilderParams> = {
    kind: 'shape',
    params: state.params,
    center(): Position {
      return add(state.params.center, state.translate)
    },
    
    bbox(): { min: Position; max: Position } {
      const c = self.center()
      // If we have rotation, compute rotated AABB; otherwise fast path
      const rot = effectiveRotation(state.params)
      if (!rot) return getShapeBounds(state.params.type, c, state.params.radius, state.params.height, state.params.axis || 'y')
      const base = getShapeBounds(state.params.type, state.params.center, state.params.radius, state.params.height, 'y')
      const q = new Quaternion(rot.x, rot.y, rot.z, rot.w)
      const corners: Array<Vector3> = []
      ;[
        [base.min.x, base.min.y, base.min.z],
        [base.min.x, base.min.y, base.max.z],
        [base.min.x, base.max.y, base.min.z],
        [base.min.x, base.max.y, base.max.z],
        [base.max.x, base.min.y, base.min.z],
        [base.max.x, base.min.y, base.max.z],
        [base.max.x, base.max.y, base.min.z],
        [base.max.x, base.max.y, base.max.z],
      ].forEach(([x,y,z]) => {
        const v = new Vector3(x - state.params.center.x, y - state.params.center.y, z - state.params.center.z)
        v.applyQuaternion(q)
        v.add(new Vector3(c.x, c.y, c.z))
        corners.push(v)
      })
      let minX = Infinity, minY = Infinity, minZ = Infinity
      let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
      for (const v of corners) {
        if (v.x < minX) minX = v.x; if (v.y < minY) minY = v.y; if (v.z < minZ) minZ = v.z
        if (v.x > maxX) maxX = v.x; if (v.y > maxY) maxY = v.y; if (v.z > maxZ) maxZ = v.z
      }
      return {
        min: { x: Math.floor(minX), y: Math.floor(minY), z: Math.floor(minZ) },
        max: { x: Math.ceil(maxX), y: Math.ceil(maxY), z: Math.ceil(maxZ) },
      }
    },
    onTranslate(t: Translate): Builder<ShapeBuilderParams> {
      return makeShapeBuilder(state.params, t)
    },
    getTranslate(): Position {
      return state.translate || { x: 0, y: 0, z: 0 }
    },
    previewEvents(): EventTuple[] {
      const c = self.center()
      const p = state.params
      return [[
        'geom.shape',
        shapeEventPayload(p, c)
      ]]
    },
    toCommitEvents(): EventTuple[] {
      const c = self.center()
      const p = state.params
      return [[
        'geom.shape',
        shapeEventPayload(p, c)
      ]]
    },
    onRotate(axis: 'x'|'y'|'z', deltaRad: number) {
      const rot = state.params.rotation || IDENTITY_ROTATION
      let q = new Quaternion(rot.x, rot.y, rot.z, rot.w)
      const axisVec = axis === 'x' ? new Vector3(1,0,0) : axis === 'y' ? new Vector3(0,1,0) : new Vector3(0,0,1)
      const dq = new Quaternion().setFromAxisAngle(axisVec.normalize(), deltaRad)
      // Pre-multiply to rotate in world coordinates around global axis
      q = dq.multiply(q).normalize()
      const next: ShapeBuilderParams = { ...state.params, rotation: toLiteral(q) }
      return makeShapeBuilder(next, state.translate)
    },
  }

  return self
}
